import React from 'react'
import PropTypes from 'prop-types'
import { get, isEmpty } from 'lodash'
import { Typography } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import loadable from '@loadable/component'

import AsOfDate from '../AsOfDate'
import Spinner from '../Loading/Spinner'
import {
  AMCHART_WAFFLE_COLORS,
  AMCHART_WAFFLE_COLORS_LENGTH,
  NO_DATA_MESSAGE,
} from '../../utils/constants'
import { GOLDEN_BASE } from '../../theme/variables/colors'
import { formatPercentOneDecimal } from '../../utils/functions'

const LoadableWaffleChart = loadable(() => import('./WaffleChart'), {
  fallback: <Spinner />,
  ssr: false,
})

const Container = styled('div')(({ theme, $columnReverse }) => ({
  display: 'flex',
  flexDirection: $columnReverse ? 'column-reverse' : 'row-reverse',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column !important',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 20,
  },
}))

const LegendContainer = styled('div')(({
  theme,
  $dataLength,
  $hasBenchmark,
}) => {
  let height = '700px'
  if ($dataLength > 12 && $hasBenchmark) {
    height = `${($dataLength * 104) / 3}px`
  } else if ($dataLength > 12 && !$hasBenchmark) {
    height = `${($dataLength * 84) / 3}px`
  } else if ($dataLength <= 6) {
    height = `${($dataLength * 104) / 2}px`
  }
  const width = $dataLength > 12 ? '100%' : '60%'
  return {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    height,
    width,
    [theme.breakpoints.down('md')]: {
      height: '100% !important',
      width: '100% !important',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
    },
  }
})

const LegendItem = styled('div')(() => ({
  display: 'flex',
  marginTop: '24px',
}))

const LegendBullet = styled('div')(({ color }) => ({
  width: '16px',
  height: '16px',
  marginRight: '12px',
  backgroundColor: color,
}))

const LegendText = styled(Typography)(() => ({
  fontWeight: 700,
  lineHeight: '20px',
}))

const ChartContainer = styled('div')(({ theme }) => ({
  width: '40%',
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
    width: '100% !important',
  },
}))

const FundChartContainer = styled('div')(({ $showBenchmark }) => ({
  paddingBottom: '38px',
  marginBottom: '38px',
  marginTop: '24px',
  borderBottom: $showBenchmark ? `1px solid ${GOLDEN_BASE}` : 'none',
  '@media (max-width:600px)': {
    paddingBottom: '16px',
    marginBottom: '16px',
    marginTop: '0px',
  },
}))

const LastDivText = styled('div')(() => ({
  marginBottom: '24px',
  marginTop: '24px',
}))

const LastDivTypography = styled(Typography)(() => ({
  fontWeight: '600',
  fontSize: '13px',
}))

function SectorAllocations({ data, ticker }) {
  const theme = useTheme()

  const sectorAllocations =
    get(data, 'sectorAllocation') || get(data, 'sector_allocation') || []
  const asOfDate = get(sectorAllocations, '[0].calendar.date', '')

  const mutualFundTicker = get(data, 'fundClasses[3].ticker', '')
  const etfFundTicker = get(data, 'fundClasses[0].ticker', '')
  const fundTicker = mutualFundTicker || etfFundTicker || ticker

  const fundName = get(data, 'name', 'Fund')
  const tableData = sectorAllocations.map((item) => ({
    name: item.name,
    fund: item.fundValue || item.value,
    benchmark: item.benchmarkValue,
  }))

  if (isEmpty(tableData)) {
    return <Typography>{NO_DATA_MESSAGE}</Typography>
  }

  const hasBenchmark = tableData.some((row) => row.benchmark)
  const dataLength = tableData.length
  const fundHeader = `${fundName} (${fundTicker})`
  const benchmarkHeader = hasBenchmark
    ? sectorAllocations[0].benchmarkIndex.name
    : 'Benchmark'

  return (
    <>
      {asOfDate && <AsOfDate date={asOfDate} />}
      <Container $columnReverse={dataLength > 12}>
        <ChartContainer>
          {tableData.some((row) => row.fund) && (
            <FundChartContainer $showBenchmark={hasBenchmark}>
              <Typography variant="body2" fontWeight={700}>
                {fundHeader}
              </Typography>
              <LoadableWaffleChart
                data={tableData}
                chartDiv="fund-chart"
                chartType="fund"
              />
            </FundChartContainer>
          )}
          {hasBenchmark && (
            <div>
              <Typography variant="body2" fontWeight={700} mt={2}>
                {benchmarkHeader}
              </Typography>
              <LoadableWaffleChart
                data={tableData}
                chartDiv="benchmark-chart"
                chartType="benchmark"
              />
            </div>
          )}
        </ChartContainer>

        <LegendContainer $dataLength={dataLength} $hasBenchmark={hasBenchmark}>
          {tableData.map((obj, index) => (
            <LegendItem key={obj.name}>
              <LegendBullet
                color={
                  AMCHART_WAFFLE_COLORS[index % AMCHART_WAFFLE_COLORS_LENGTH]
                }
              />
              <div>
                <LegendText>{obj.name}</LegendText>
                <Typography variant="body2">
                  {`${fundTicker}: ${formatPercentOneDecimal(obj.fund)}%`}
                </Typography>
                {obj.benchmark && (
                  <Typography variant="body2">
                    {`Benchmark: ${formatPercentOneDecimal(obj.benchmark)}%`}
                  </Typography>
                )}
              </div>
            </LegendItem>
          ))}
        </LegendContainer>
      </Container>
      <LastDivText>
        {tableData.some((row) => row.fund) && (
          <LastDivTypography>{`${fundTicker}: ${fundName}`}</LastDivTypography>
        )}
        {hasBenchmark && (
          <LastDivTypography>{`Benchmark: ${benchmarkHeader}`}</LastDivTypography>
        )}
      </LastDivText>
    </>
  )
}

SectorAllocations.propTypes = {
  data: PropTypes.shape({
    sectorAllocation: PropTypes.array,
    sector_allocation: PropTypes.array,
    fundClasses: PropTypes.array,
    name: PropTypes.string,
  }),
  ticker: PropTypes.string,
}

export default SectorAllocations
